import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, X } from 'lucide-react';
import useOverlayHistory from 'services/useOverlayHistory';
import useScrollLock from 'services/useScrollLock';
import { toast } from 'react-hot-toast';

function tagsToList(tags) {
  if (tags == null) return [];
  if (Array.isArray(tags)) {
    return tags.map((t) => (typeof t === 'string' ? t : JSON.stringify(t))).map((t) => t.trim()).filter(Boolean);
  }
  if (typeof tags === 'object') {
    return Object.entries(tags)
      .map(([key, value]) => `${key}: ${typeof value === 'string' ? value : JSON.stringify(value)}`)
      .map((t) => t.trim())
      .filter(Boolean);
  }
  const single = String(tags).trim();
  return single ? [single] : [];
}

/** Backend may expose `column` (pk), `column_id`, or a nested object; avoid "[object Object]" → NaN → JSON null clearing the FK */
export function columnPkFromCard(card) {
  if (!card) return null;
  const v = card.column_id ?? card.column;
  if (v == null || v === '') return null;
  if (typeof v === 'object') {
    if (v.id == null) return null;
    if (String(v.id).startsWith('temp_')) return v.id;
    const nested = Number(v.id);
    return Number.isFinite(nested) ? nested : null;
  }
  if (typeof v === 'string' && v.startsWith('temp_')) return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function DrawerCardForm({
  isOpen,
  onClose,
  onSubmit,
  onDelete,
  initial = null,
  submitting,
  columns = [],
  defaultColumnId = null,
  projectId,
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [color, setColor] = useState('#E5E7EB');
  const [columnId, setColumnId] = useState('');
  const [tags, setTags] = useState([]);
  const [tagDraft, setTagDraft] = useState('');
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef(null);
  const actionsRef = useRef({ submitting: false, dirty: () => false, save: () => false, requestClose: () => {} });
  const requestClose = useOverlayHistory(isOpen, onClose, { stateKey: 'qappCardForm' });

  useScrollLock(isOpen);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (isOpen) {
      setShouldRender(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else {
      setIsVisible(false);
      timeoutRef.current = setTimeout(() => {
        setShouldRender(false);
        timeoutRef.current = null;
      }, 300);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (initial) {
      setTitle(initial.title || '');
      setDescription(initial.description || '');
      setDueDate(initial.due_date || '');
      setColor(initial.color || '#E5E7EB');
      const pk = columnPkFromCard(initial);
      setColumnId(
        pk != null ? String(pk) : defaultColumnId != null && defaultColumnId !== '' ? String(defaultColumnId) : ''
      );
      setTags(tagsToList(initial.tags));
      setTagDraft('');
    } else {
      setTitle('');
      setDescription('');
      setDueDate('');
      setColor('#E5E7EB');
      setColumnId(defaultColumnId != null ? String(defaultColumnId) : '');
      setTags([]);
      setTagDraft('');
    }
  }, [isOpen, initial, defaultColumnId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      const actions = actionsRef.current;
      if (actions.submitting) return;
      e.preventDefault();
      if (!actions.dirty()) {
        actions.requestClose();
        return;
      }
      actions.save();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  const baselineColumnId =
    initial == null
      ? defaultColumnId != null ? String(defaultColumnId) : ''
      : (() => {
          const pk = columnPkFromCard(initial);
          if (pk != null) return String(pk);
          return defaultColumnId != null && defaultColumnId !== '' ? String(defaultColumnId) : '';
        })();
  const draftTag = tagDraft.trim();
  const tagPayload = draftTag && !tags.includes(draftTag) ? [...tags, draftTag] : tags;
  const baselineTags = initial ? tagsToList(initial.tags) : [];
  const tagsMatch = tagPayload.length === baselineTags.length && tagPayload.every((tag, i) => tag === baselineTags[i]);
  const dirty =
    title.trim() !== (initial?.title || '').trim() ||
    description.trim() !== (initial?.description || '').trim() ||
    (dueDate || '') !== (initial?.due_date || '') ||
    color.toLowerCase() !== (initial?.color || '#E5E7EB').toLowerCase() ||
    String(columnId) !== baselineColumnId ||
    !tagsMatch;

  const saveCard = () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return false;
    }
    let column = null;
    if (columnId !== '' && columnId != null) {
      if (String(columnId).startsWith('temp_')) {
        column = columnId;
      } else {
        column = Number(columnId);
        if (!Number.isFinite(column)) {
          toast.error('Invalid column');
          return false;
        }
      }
    }
    onSubmit({
      project: projectId,
      title: title.trim(),
      description: description.trim(),
      tags: tagPayload,
      due_date: dueDate || null,
      color,
      column,
    });
    return true;
  };

  actionsRef.current = { submitting, dirty: () => dirty, save: saveCard, requestClose };

  if (!shouldRender) return null;

  const commitTag = (draft = tagDraft) => {
    const next = draft.trim();
    if (!next) return;
    setTags((prev) => (prev.includes(next) ? prev : [...prev, next]));
    setTagDraft('');
  };

  const removeTag = (index) => setTags((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = (e) => {
    e.preventDefault();
    saveCard();
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-50 ${isVisible ? 'opacity-30' : 'opacity-0'}`}
        onMouseDown={requestClose}
      />
      <div
        className={`fixed top-0 right-0 h-full w-full md:w-[60%] max-w-[800px] bg-white shadow-xl z-[60] transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit} className="h-full flex flex-col">
          <div
            className="flex items-center justify-between px-4 py-2 bg-white border-b shrink-0"
            style={{ borderBottomColor: color || '#E5E7EB', borderBottomWidth: '3px' }}
          >
            <button type="button" onClick={requestClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Close">
              <ChevronLeft className="h-5 w-5 text-gray-600" strokeWidth={2} />
            </button>
            <h2 className="text-xl font-semibold text-gray-800 flex-1 text-center truncate px-2">
              {initial ? 'Edit card' : 'New card'}
            </h2>
            <div className="w-9" />
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            <div className="flex items-center gap-3">
              <input
                className="min-w-0 flex-1 border rounded-xl px-3 py-2 text-sm"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
                placeholder="Title"
                aria-label="Title"
                autoFocus
              />
              <label className="relative h-8 w-8 shrink-0 cursor-pointer" title="Color">
                <span
                  className="block h-8 w-8 rounded-full border-2 border-gray-300 shadow-sm"
                  style={{ backgroundColor: color }}
                />
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                  aria-label="Color"
                />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="date"
                className="w-full border rounded-xl px-3 py-2 text-sm"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                aria-label="Due date"
              />
              <select
                className="w-full border rounded-xl px-3 py-2 text-sm"
                value={columnId}
                onChange={(e) => setColumnId(e.target.value)}
                aria-label="Column"
              >
                <option value="">No column</option>
                {columns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 rounded-xl border px-2 py-1.5">
              {tags.map((tag, i) => (
                <span key={`${tag}-${i}`} className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                  <span className="max-w-[12rem] truncate">{tag}</span>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => removeTag(i)}
                    className="text-gray-400 hover:text-gray-700"
                    aria-label={`Remove ${tag}`}
                  >
                    <X className="h-3 w-3" strokeWidth={2} />
                  </button>
                </span>
              ))}
              <input
                className="min-w-[5rem] flex-1 bg-transparent px-1 py-0.5 text-sm outline-none"
                value={tagDraft}
                onChange={(e) => setTagDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    commitTag();
                  } else if (e.key === 'Backspace' && !tagDraft && tags.length) {
                    removeTag(tags.length - 1);
                  }
                }}
                onBlur={() => commitTag()}
                placeholder={tags.length ? '' : 'Tags'}
                aria-label="Tags"
              />
            </div>
            <textarea
              className="w-full border rounded-xl px-3 py-2 text-sm min-h-[200px]"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description"
              aria-label="Description"
            />
          </div>

          <div className="flex justify-between gap-2 px-4 py-3 border-t bg-white shrink-0">
            <div>
              {initial && onDelete && (
                <button
                  type="button"
                  onClick={onDelete}
                  disabled={submitting}
                  className="px-4 py-2 rounded-xl border border-red-200 text-red-700 hover:bg-red-50 text-sm disabled:opacity-50"
                >
                  Delete
                </button>
              )}
            </div>
            <div className="flex gap-2 ml-auto">
              <button type="button" onClick={requestClose} className="px-4 py-2 rounded-xl border border-gray-300">
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !title.trim()}
                className="px-4 py-2 rounded-xl bg-blue-700 text-white disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

export default DrawerCardForm;
